import React from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { useDrawerDirty } from "@shell/components/DrawerShell";
import { SelectCard } from "@shell/components/SelectCard";

import type { ProvisionModeUI } from "../../../types";

import { useDatabaseForm, type DatabaseFormState } from "./useDatabaseForm";

const HA_REPLICA_OPTIONS: {
  value: "1+0" | "1+1" | "1+2" | "1+3";
  label: string;
  desc: string;
}[] = [
  { value: "1+0", label: "1+0", desc: "Single instance (no HA)" },
  { value: "1+1", label: "1+1", desc: "Primary + 1 standby" },
  { value: "1+2", label: "1+2", desc: "Primary + 2 standbys" },
  { value: "1+3", label: "1+3", desc: "Primary + 3 standbys" },
];

const PG_VERSIONS: { value: "15" | "16" | "17"; label: string }[] = [
  { value: "15", label: "PostgreSQL 15" },
  { value: "16", label: "PostgreSQL 16" },
  { value: "17", label: "PostgreSQL 17" },
];

const EXTENSION_OPTIONS = ["uuid-ossp", "pg_trgm", "postgis", "pgvector"];

export interface DatabaseFormHandle {
  submit: () => boolean;
  getState: () => DatabaseFormState;
}

export interface DatabaseFormProps {
  projectAlias: string;
  onSubmit: (req: import("../../../types").CreateDatabaseRequest) => void;
  isSubmitting: boolean;
}

export const DatabaseForm = React.forwardRef<
  DatabaseFormHandle,
  DatabaseFormProps
>(function DatabaseForm(
  { projectAlias, onSubmit, isSubmitting },
  ref,
): React.ReactElement {
  const { setIsDirty } = useDrawerDirty();
  const form = useDatabaseForm(projectAlias);

  const handleSubmit = () => {
    if (!form.validate()) return false;
    onSubmit(form.buildRequest());
    return true;
  };

  React.useImperativeHandle(ref, () => ({
    submit: handleSubmit,
    getState: () => form.state,
  }));

  return (
    <Stack spacing={2.5}>
      {/* Name */}
      <Box>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
          Database Name
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="my_database"
          value={form.state.name}
          onChange={(e) => {
            form.setField("name", e.target.value);
            setIsDirty(true);
          }}
          onBlur={() => form.onBlur("name")}
          error={!!form.errors.name}
          helperText={form.errors.name}
          disabled={isSubmitting}
        />
      </Box>

      {/* Mode selector */}
      <Box>
        <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
          Mode
        </Typography>
        <Stack spacing={1}>
          <SelectCard
            label="Shared"
            description="Logical database on the shared Rusha CNPG cluster"
            selected={form.state.mode === "shared"}
            onClick={() => {
              form.setField("mode", "shared" as ProvisionModeUI);
              setIsDirty(true);
            }}
            disabled={isSubmitting}
          />
          <SelectCard
            label="Dedicated"
            description="Own CNPG cluster with dedicated resources and backup"
            selected={form.state.mode === "dedicated"}
            onClick={() => {
              form.setField("mode", "dedicated" as ProvisionModeUI);
              setIsDirty(true);
            }}
            disabled={isSubmitting}
          />
        </Stack>
      </Box>

      {/* Dedicated-only fields */}
      {form.state.mode === "dedicated" && (
        <Stack spacing={2.5} sx={{ animation: "fadeIn 200ms ease" }}>
          {/* HA Replicas */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              High Availability
            </Typography>
            <Stack spacing={1}>
              {HA_REPLICA_OPTIONS.map((opt) => (
                <SelectCard
                  key={opt.value}
                  label={opt.label}
                  description={opt.desc}
                  selected={form.state.haReplicas === opt.value}
                  onClick={() => {
                    form.setField("haReplicas", opt.value);
                    setIsDirty(true);
                  }}
                  disabled={isSubmitting}
                />
              ))}
            </Stack>
          </Box>

          {/* Storage */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              Storage Size (Gi)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={form.state.storageSizeGi}
              onChange={(e) => {
                form.setField("storageSizeGi", Number(e.target.value));
                setIsDirty(true);
              }}
              disabled={isSubmitting}
              inputProps={{ min: 1, max: 100 }}
            />
          </Box>

          {/* PostgreSQL Version */}
          <Box>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              PostgreSQL Version
            </Typography>
            <Stack spacing={1}>
              {PG_VERSIONS.map((v) => (
                <SelectCard
                  key={v.value}
                  label={v.label}
                  selected={form.state.postgresVersion === v.value}
                  onClick={() => {
                    form.setField("postgresVersion", v.value);
                    setIsDirty(true);
                  }}
                  disabled={isSubmitting}
                />
              ))}
            </Stack>
          </Box>

          {/* Advanced: Extensions */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2" fontWeight={500}>
                Advanced
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mb: 1, display: "block" }}
              >
                Extensions
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                {EXTENSION_OPTIONS.map((ext) => {
                  const selected = form.state.extensions.includes(ext);
                  return (
                    <Chip
                      key={ext}
                      label={ext}
                      size="small"
                      variant={selected ? "filled" : "outlined"}
                      color={selected ? "primary" : "default"}
                      onClick={() => {
                        const next = selected
                          ? form.state.extensions.filter((e) => e !== ext)
                          : [...form.state.extensions, ext];
                        form.setField("extensions", next);
                        setIsDirty(true);
                      }}
                      sx={{ cursor: "pointer" }}
                    />
                  );
                })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      )}

      {form.state.mode === "shared" && (
        <Alert
          severity="info"
          sx={{ "& .MuiAlert-message": { fontSize: "0.875rem" } }}
        >
          Shared mode provisions a logical database on the Rusha CNPG cluster.
          You get a dedicated role and database with pooled access via
          PgBouncer.
        </Alert>
      )}

      <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </Stack>
  );
});

DatabaseForm.displayName = "DatabaseForm";
